import { useForm, Controller } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../components/forms/button.component';
import useModal from '../../../../hooks/use-close-modal.hook';
import Icon from '../../../../components/forms/icon.component';
import { LocalStorageService } from '../../../../services/local-storage.service';
import PhoneInput from 'react-phone-input-2';
import Title from '../../../../components/forms/titles.component';
import ReCAPTCHA from 'react-google-recaptcha';
import { AlertService } from '../../../../services/alert.service';
import { useDarkMode } from '../../../../hooks/use-dark-mode.hook';
interface SendMessageRCSProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload?: any;
  onSent?: () => void;
}

function SendMessageRCS({ show, onRequestClose, payload, onSent }: SendMessageRCSProps) {
  const { t } = useTranslation();
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const isDarkTheme = useDarkMode();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [isCoolDownActive, setIsCoolDownActive] = useState(false);
  const [coolDownTimeLeft, setCoolDownTimeLeft] = useState(30);
  const modalRef = useRef<HTMLDivElement>(null);
  const defaultValues = {
    number: '+52'
  };
  useModal(modalRef, show);

  const handleCaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const handleTimer = () => {
    const now = Date.now();
    LocalStorageService.save('sendCoolDownStartTime', now);
    setIsCoolDownActive(true);
    setCoolDownTimeLeft(30);
    const interval = setInterval(() => {
      const elapsed = Date.now() - now;
      const secondsLeft = Math.ceil((30000 - elapsed) / 1000);
      if (secondsLeft <= 0) {
        clearInterval(interval);
        setIsCoolDownActive(false);
        setCoolDownTimeLeft(30);
        LocalStorageService.clearField('sendCoolDownStartTime');
      } else {
        setCoolDownTimeLeft(secondsLeft);
      }
    }, 1000);
    if (typeof onSent === 'function') {
      onSent();
    }
  };

  const submit = async (data: any, buttonID: string) => {
    if (!recaptchaToken) {
      AlertService.toastInfo('¡ Atención !, debes completar el reCAPTCHA.');
      return;
    }

    const isConfirmed = await AlertService.question(
      t('general.atention'),
      '¿Estas seguro de enviar mensaje a' + '<br><br>' + `<b>${watch('number')}</b>` + '?',
      () => Promise.resolve(true)
    );
    if (!isConfirmed) {
      return;
    }
    try {
      handleTimer();
      resetForm();
      AlertService.toastSuccess('¡Se ha enviado el mensaje correctamente!');
    } catch (errorCode: any) {
      console.log('errorCode:', errorCode);
      AlertService.toastError(errorCode.error || errorCode.message);
    }
    const updatedValues: any = {
      phone_number: `${data.number}`,
      recaptcha: recaptchaToken
    };
  };

  const resetForm = () => {
    onRequestClose(false);
    recaptchaRef.current?.reset();
    reset({ number: '+52' });
  };

  useEffect(() => {
    const coolDownStartTime = LocalStorageService.get('sendCoolDownStartTime');
    if (coolDownStartTime) {
      const elapsed = Date.now() - parseInt(coolDownStartTime, 10);
      if (elapsed < 30000) {
        setIsCoolDownActive(true);
        setCoolDownTimeLeft(Math.ceil((30000 - elapsed) / 1000));
        const interval = setInterval(() => {
          const newElapsed = Date.now() - parseInt(coolDownStartTime, 10);
          const secondsLeft = Math.ceil((30000 - newElapsed) / 1000);
          if (secondsLeft <= 0) {
            clearInterval(interval);
            setIsCoolDownActive(false);
            setCoolDownTimeLeft(30);
            LocalStorageService.clearField('sendCoolDownStartTime');
          } else {
            setCoolDownTimeLeft(secondsLeft);
          }
        }, 1000);
        return () => clearInterval(interval);
      } else {
        LocalStorageService.clearField('sendCoolDownStartTime');
      }
    }
  }, []);

  return (
    <div className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog mw-600px">
        <div className="modal-content">
          <div className="modal-header justify-content-end border-0 pb-0">
            <div
              className="btn btn-sm btn-icon btn-active-light-primary"
              onClick={() => resetForm()}
            >
              <Icon icon="ki-cross" size="fs-2x" />
            </div>
          </div>
          <form
            id="addUserForm"
            onSubmit={handleSubmit((data) => {
              const buttonID = 'sendMessageRCSButton-72338492';
              submit(data, buttonID);
            })}
          >
            <div className="modal-body pt-0 pb-15 px-5 px-xl-20">
              <div className="mt-1 text-center">
                <h1 className="mb-3">
                  ENVIAR MENSAJE RCS{' '}
                  <a className="link-primary fw-bold ms-2" href="#">
                    {payload?.name ? payload.name : ''}
                  </a>
                </h1>
                <div className="text-muted fw-semibold fs-5">
                  <span
                    dangerouslySetInnerHTML={{ __html: t('channels.rcs.modal-C-Subtitle') }}
                  ></span>
                </div>
                <div className="separator border-2 my-5"></div>
              </div>
              <div className="row">
                <div className="mb-10 mb-lg-0  h-100 p-10">
                  <Title
                    title={t('Ingresa el número de teléfono')}
                    subtitle={t('Ingresa el número de teléfono al que deseas enviar el mensaje')}
                    icon="ki-send"
                  />
                  <Controller
                    control={control}
                    name="number"
                    rules={{
                      required: true,
                      validate: (value) => {
                        const cleaned = value.replace(/\D/g, '');
                        return cleaned.length === 12 || 'Número inválido';
                      }
                    }}
                    render={({ field }) => (
                      <>
                        <label className="form-label required">Número</label>
                        <PhoneInput
                          country="mx"
                          onlyCountries={['mx']}
                          value={field.value}
                          onChange={field.onChange}
                          inputClass="phone-input-custom bg-body"
                          containerClass="phone-input-container"
                          buttonClass="phone-input-button"
                          dropdownClass="phone-country-dropdown"
                          searchClass="search-box"
                          countryCodeEditable={false}
                          placeholder="55 1234 5678"
                        />
                      </>
                    )}
                  />
                  <div className="d-flex justify-content-center mt-5">
                    <ReCAPTCHA
                      sitekey="6Lfec20rAAAAANyGkUlot_jUEWAbexPdSk8Nw_lu"
                      onChange={handleCaptchaChange}
                      theme={isDarkTheme ? 'dark' : 'light'}
                      ref={recaptchaRef}
                    />
                  </div>
                </div>
              </div>
              <div className="d-flex flex-center flex-row-fluid pt-12">
                <button
                  type="button"
                  className="btn btn-light hover-scale"
                  onClick={() => resetForm()}
                >
                  {t('general.close')}
                </button>
                <Button
                  type="submit"
                  id="sendData"
                  className="btn btn-primary ms-3 hover-scale"
                  disabled={!isValid || isCoolDownActive}
                  isLoading={isSubmitting}
                >
                  {isCoolDownActive ? `Volver a enviar en ${coolDownTimeLeft}s` : 'Enviar Mensaje'}
                  <i className="ki-duotone ki-send ms-2 fs-1">
                    <span className="path1"></span>
                    <span className="path2"></span>
                  </i>
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SendMessageRCS;
